<script setup>
import { ref, watch, nextTick, onMounted } from 'vue';
import { useModalStore } from '@/store/modal.store';

import UserProfileEdit from '@/components/block/modal/UserProfileEdit.vue';
import CompanyProfileEdit from '@/components/block/modal/CompanyProfileEdit.vue';
import UnderlyingData from '@/components/block/modal/UnderlyingData.vue';
import CompanyOnboarding from '@/components/block/modal/CompanyOnboarding.vue'
import NewLink from '@/components/block/modal/NewLink.vue';
import RenameLink from '@/components/block//modal/RenameLink.vue';
import DeviceInfoEdit from '@/components/block/modal/DeviceInfoEdit.vue';
import ClientInfoEdit from '@/components/block/modal/ClientInfoEdit.vue';
import SubscriberProfileEdit from '@/components/block/modal/SubscriberProfileEdit.vue';
import MarketingNewLink from '@/components/block/modal/MarketingNewLink.vue';
import DeleteMarketingLink from '@/components/block/modal/DeleteMarketingLink.vue';
import CompleteDeviceSetup from '@/components/block/modal/CompleteDeviceSetup.vue';
import AddNewDevice from '@/components/block/modal/AddNewDevice.vue';
import NewUser from '@/components/block/modal/NewUser.vue';

import TeamNewUser from '@/components/block/modal/TeamNewUser.vue';
import SuccessfullyReset from '@/components/block/modal/SuccessfullyReset.vue';
import PasswordReset from '@/components/block/modal/PasswordReset.vue';
import ClientPasswordReset from '@/components/block/modal/ClientPasswordReset.vue';
import NewPhoneNumber from '@/components/block/modal/NewPhoneNumber.vue';
import NewPhoneEnterCode from '@/components/block/modal/NewPhoneEnterCode.vue';
import NewEmailAddress from '@/components/block/modal/NewEmailAddress.vue';
import FactorCreateNewSuccessfullyReset from '@/components/block/modal/FactorCreateNewSuccessfullyReset.vue';
import FactorCreateNewPassword from '@/components/block/modal/FactorCreateNewPassword.vue';
import FactorCreateNewEnterCode from '@/components/block/modal/FactorCreateNewEnterCode.vue';
import FactorCreateNewAuthentication from '@/components/block/modal/FactorCreateNewAuthentication.vue';
import FactorAuthentication from '@/components/block/modal/FactorAuthentication.vue';
import DeviceAlerts from '@/components/block/modal/DeviceAlerts.vue';
import CreateNewSuccessfullyReset from '@/components/block/modal/CreateNewSuccessfullyReset.vue';
import CreateNewPassword from '@/components/block/modal/CreateNewPassword.vue';
import CreateNewEnterCode from '@/components/block/modal/CreateNewEnterCode.vue';
import CreateNewAuthentication from '@/components/block/modal/CreateNewAuthentication.vue';
import ActivateUser from "@/components/block/modal/ActivateUser.vue";
import DeactivateUser from "@/components/block/modal/DeactivateUser.vue";
import ActivateGroup from "@/components/block/modal/ActivateGroup.vue";
import DeactivateGroup from "@/components/block/modal/DeactivateGroup.vue";
import BigShipmentInfocardModal from "@/components/block/modal/BigShipmentInfocardModal.vue";
import ChangeTemplateFile from "@/components/block/modal/ChangeTemplateFile.vue";
import { useRouter } from "vue-router";
import { onUnmounted } from "@vue/runtime-dom";

const router = useRouter();

router.beforeEach(() => {
  close();
})

const modalStore = useModalStore();

function isModalVisible(modalIndex) {
  return modalStore.isModalVisible(modalIndex);
}

watch(() => modalStore.modalToShow, (newValue) => {
  if (newValue) {
    document.body.classList.add('no-scroll');
  } else {
    document.body.classList.remove('no-scroll');
  }
});

const closeSignal = ref(false);
const close = () => {
  closeSignal.value = false;
  nextTick().then(() => {
    closeSignal.value = true;
  });
};

const handleEscapeKey = (event) => {
  if (event.key === 'Escape') {
    close();
  }
};

onMounted(() => {
  window.addEventListener('keydown', handleEscapeKey);
});

onUnmounted(() => {
  window.removeEventListener('keydown', handleEscapeKey);
});

</script>

<template>
  <Transition name="fade">
    <div v-if="modalStore.modalToShow" class="modal_overlay" @click="close"></div>
  </Transition>

  <Transition name="bounce">
    <UserProfileEdit
      v-if="isModalVisible('UserProfileEdit') || isModalVisible('ProfileInformationUpdated') || isModalVisible('ProfileEmailUpdated') || isModalVisible('ProfileInformationNoUpdated')"
      :close-signal="closeSignal" />
  </Transition>

  <Transition name="bounce">
    <CompanyProfileEdit
      v-if="isModalVisible('CompanyProfileEdit') || isModalVisible('CompanyInformationNoUpdated') || isModalVisible('CompanyInformationUpdated')"
      :close-signal="closeSignal" />
  </Transition>

  <Transition name="bounce">
    <CompanyOnboarding
      v-if="isModalVisible('CompanyOnboarding') || isModalVisible('step2') || isModalVisible('step3') || isModalVisible('step4') || isModalVisible('step5') || isModalVisible('step6')"
      :close-signal="closeSignal" />
  </Transition>

  <Transition name="bounce">
    <NewUser
      v-if="modalStore.isModalVisible('NewUser') || modalStore.isModalVisible('NewUserStep2') || modalStore.isModalVisible('NewUserStep3') || modalStore.isModalVisible('NewUserStep4')"
      :close-signal="closeSignal" />
  </Transition>

  <Transition name="bounce">
    <UnderlyingData v-if="isModalVisible('UnderlyingData')" :close-signal="closeSignal" />
  </Transition>

  <Transition name="bounce">
    <TeamNewUser v-if="isModalVisible('TeamNewUser')" :close-signal="closeSignal" />
  </Transition>

  <Transition name="bounce">
    <NewLink v-if="isModalVisible('NewLink')" :close-signal="closeSignal" />
  </Transition>

  <Transition name="bounce">
    <RenameLink v-if="isModalVisible('RenameLink')" :close-signal="closeSignal" />
  </Transition>

  <Transition name="bounce">
    <PasswordReset
      v-if="modalStore.isModalVisible('ResetPasswordUser') || modalStore.isModalVisible('CodePasswordUser') || modalStore.isModalVisible('ResetPasswordCode')"
      :close-signal="closeSignal" />
  </Transition>

  <Transition name="bounce">
    <ClientPasswordReset
      v-if="modalStore.isModalVisible('ClientResetPasswordUser') || modalStore.isModalVisible('ClientCodePasswordUser') || modalStore.isModalVisible('ClientResetPasswordCode')"
      :close-signal="closeSignal" />
  </Transition>

  <Transition name="bounce">
    <ActivateUser v-if="isModalVisible('ActivateUser')" :close-signal="closeSignal" />
  </Transition>

  <Transition name="bounce">
    <DeactivateUser v-if="isModalVisible('DeactivateUser')" :close-signal="closeSignal" />
  </Transition>

  <Transition name="bounce">
    <ActivateGroup v-if="isModalVisible('ActivateGroup')" :close-signal="closeSignal" />
  </Transition>

  <Transition name="bounce">
    <DeactivateGroup v-if="isModalVisible('DeactivateGroup')" :close-signal="closeSignal" />
  </Transition>

  <Transition name="bounce">
    <DeviceInfoEdit
      v-if="isModalVisible('DeviceInfoEdit') || isModalVisible('DeviceInfoUpdated') || isModalVisible('DeviceInfoNoUpdated')"
      :close-signal="closeSignal" />
  </Transition>


  <Transition name="bounce">
    <ClientInfoEdit
      v-if="isModalVisible('ClientInfoEdit') || isModalVisible('ClientInfoUpdated') || isModalVisible('ClientInfoNoUpdated') || isModalVisible('ClientProfileEmailUpdated')"
      :close-signal="closeSignal" />
  </Transition>

  <Transition name="bounce">
    <SubscriberProfileEdit
      v-if="isModalVisible('SubscriberProfileEdit') || isModalVisible('SubscriberInfoUpdated') || isModalVisible('SubscriberInfoNoUpdated')"
      :close-signal="closeSignal" />
  </Transition>

  <Transition name="bounce">
    <MarketingNewLink
      v-if="isModalVisible('MarketingNewLink') || isModalVisible('MarketingNewLinkDownload') || isModalVisible('MarketingNewLinkUpload')"
      :close-signal="closeSignal" />
  </Transition>

  <Transition name="bounce">
    <DeleteMarketingLink
      v-if="isModalVisible('DeleteMarketingLink')"
      :close-signal="closeSignal" />
  </Transition>

  <Transition name="bounce">
    <CompleteDeviceSetup
      v-if="isModalVisible('CompleteDeviceSetup')"
      :close-signal="closeSignal" />
  </Transition>

  <Transition name="bounce">
    <AddNewDevice
      v-if="isModalVisible('AddNewDevice')"
      :close-signal="closeSignal" />
  </Transition>

  <Transition name="bounce">
    <BigShipmentInfocardModal v-if="isModalVisible('BigShipmentInfocardModal')" :close-signal="closeSignal" />
  </Transition>

    <Transition name="bounce">
    <ChangeTemplateFile v-if="isModalVisible('ChangeTemplateFile')" :close-signal="closeSignal" />
  </Transition>

  <!--<TeamNewUser/>

	<SuccessfullyReset/>

	<NewPhoneNumber/>

	<NewPhoneEnterCode/>

	<NewEmailAddress/>

	<FactorCreateNewSuccessfullyReset/>

	<FactorCreateNewPassword/>

	<FactorCreateNewEnterCode/>

	<FactorCreateNewAuthentication/>

	<FactorAuthentication/>

	<DeviceAlerts/>

	<CreateNewSuccessfullyReset/>

	<CreateNewPassword/>

	<CreateNewEnterCode/>

	<CreateNewAuthentication/>-->

</template>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.35s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.bounce-enter-active {
  animation: bounce-in 0.3s;
}

.bounce-leave-active {
  animation: bounce-in 0.3s reverse;
}

@keyframes bounce-in {
  0% {
    transform: scale(0.5) translate(-50%, -50%);
    opacity: 0;
  }

  60% {
    transform: scale(1.05) translate(-50%, -50%);
  }

  100% {
    transform: scale(1) translate(-50%, -50%);
    opacity: 1;
  }
}
</style>